/**
 * @file Toast.js
 * @module src/js/modals/Toast
 * @description
 * **Component for displaying toast messages in the EDALab frontend.**
 * - Displays a message with a type (e.g., error, success).
 * - Automatically slides in and out based on timers.
 * - Can be manually closed by the user by clicking the EyeOff icon.
 * - Uses theme context to adjust styling for light/dark modes.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added manual close functionality. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-03: Improved styling and icon usage. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import { useModal } from "../contexts/ModalProvider";
import { X, Check, EyeOff, RefreshCcw } from "lucide-react";

export default function Toast({ message }) {
    const { theme } = useTheme();
    const [visible, setVisible] = useState(false);
    const [hoveredEyeOff, setHoveredEyeOff] = useState(false);
    const { removeModalMessage } = useModal();

    const presentDuration = 3000;
    const transitionDuration = 200;

    useEffect(() => {
        if (!message) return;

        // slide in
        setVisible(true);

        // start slide-out after `presentDuration`
        const hideTimer = setTimeout(() => {
            setVisible(false);
        }, presentDuration);

        // after animation, remove this toast from context
        const doneTimer = setTimeout(() => {
            removeModalMessage(message.id);
        }, presentDuration + transitionDuration);

        return () => {
            clearTimeout(hideTimer);
            clearTimeout(doneTimer);
        };
    }, [message, presentDuration, transitionDuration, removeModalMessage]);

    const isError = message.type === "error";
    const isSuccess = message.type === "success";
    const color = 
        isError ? (theme === "dark" ? "lightcoral" : "red") :
        isSuccess ? (theme === "dark" ? "lightgreen" : "green") :
            (theme === "dark" ? "lightblue" : "blue");

    return message && (
        <div
            style={{
                pointerEvents: "auto",
                padding: "6px 10px",
                border: `1.2px solid ${color}`,
                borderRadius: "10px",
                background: theme === "dark" ? "#38424cff" : "#e9e9e9ff",
                color,
                fontSize: "1rem",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                transform: visible ? "translateY(0)" : "translateY(-150%)",
                opacity: visible ? 1 : 0,
                transition: `all ${transitionDuration}ms ease-in-out`,
                maxWidth: "80vw",
                textAlign: "center",
                whiteSpace: "pre-wrap",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {isError ? <X color={color} size='1.4em' /> :
                isSuccess ? <Check color={color} size='1.4em' /> :
                <RefreshCcw color={color} size='1.4em' />}&nbsp;{message.content}&nbsp;&nbsp;
            <EyeOff size='1.2em'
                style={{
                    cursor: "pointer",
                    color: hoveredEyeOff ? color : (theme === "dark" ? "white" : "black"),
                    transition: "color 0.2s",
                }}
                onClick={() => {
                    setVisible(false);
                    setTimeout(() => {
                        removeModalMessage(message.id);
                    }, transitionDuration);
                }}
                onMouseEnter={() => setHoveredEyeOff(true)}
                onMouseLeave={() => setHoveredEyeOff(false)}
            />
        </div>
    );
}
